// Client lib imports
const businessEvents = require('client/lib/business-events')
const { send: ajax } = require('client/lib/request')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const { navigate } = require('client-v2/common/state/router/actions')
const confirmModalActions = require('client-v2/common/containers/confirm-modal/actions')
const shareModalActions = require('client-v2/common/containers/share-modal/actions')
const toastsActions = require('client-v2/common/containers/toasts/actions')
const extension = require('client-v2/common/utils/extension')

// Action enums

const USER_FOLLOWING_CHANGE_PENDING = 'USER_FOLLOWING_CHANGE_PENDING'

// Action types

const USER_FOLLOWING_CHANGE = 'USER_FOLLOWING_CHANGE'

// Action creators

function userFollowingChange(userId, isFollowing) {
  return {
    type: USER_FOLLOWING_CHANGE,
    payload: {
      userId,
      isFollowing
    }
  }
}

function userFollowingChangePending(userId) {
  return userFollowingChange(
    userId,
    USER_FOLLOWING_CHANGE_PENDING
  )
}

function userFollowingToast(user, isFollowing) {
  const message = () => tag([
    isFollowing
    ? 'You are now following '
    : 'You are no longer following ',
    tag('a', {
      href: userProfileHref(user)
    },
      `@${user.username}`
    )
  ])
  return toastsActions.showNotice({ message })
}

// Action dispatchers

function showUserActionsModal(options) {
  return async function (dispatch) {
    const action = await dispatch(confirmModalActions.show({
      heading: 'Actions for this User',
      buttons: [
        { label: 'Report this user', value: 'report' },
        { label: 'Cancel', value: '' }
      ],
      dismissOnBackdropClick: true
    }))
    switch (action) {
      case 'report':
        return dispatch(reportUser(options))
    }
  }
}

function reportUser({ user, pageContext }) {
  return async function (dispatch, getState) {
    try {
      await ajax({
        url: '/api/v0.1/reports/user',
        method: 'post',
        data: {
          context: 'flag',
          'reported_id': user.id,
          'page_context': pageContext
        }
      })
      await businessEvents.trackSegment('report-user', {
        'reported_id': user.id,
        'reporter_id': getState().user.id
      }, {
        pageContext
      })
      dispatch(confirmModalActions.alert({
        heading: 'User Reported',
        subheading: 'Got it - thanks for letting us know!'
      }))
    } catch (err) {
      dispatch(confirmModalActions.alert({
        heading: 'Sorry.',
        subheading: 'This user could not be reported at this time. Please try again later.'
      }))
      throw err
    }
  }
}

function showUserShareModal({ anchorId, user }) {
  return function (dispatch, getState) {
    const useShareModalV2 = getState().user.hasRollout('web_share_modal_v2')
    return dispatch(shareModalActions.show({
      model: user,
      anchorId: useShareModalV2 ? anchorId : null
    }))
  }
}

function toggleFollowing({ user, pageContext }) {
  return async function (dispatch) {
    const toggledFollowing = !user.isFollowing
    let method, toggleAction
    if (toggledFollowing) {
      method = 'post'
      toggleAction = 'follow'
    } else {
      method = 'delete'
      toggleAction = 'unfollow'
    }
    // Set front-end following state to "pending"
    dispatch(userFollowingChangePending(user.id))
    try {
      // Update following state on back-end
      await ajax({
        url: `/api/v0.1/user/${user.id}/${toggleAction}`,
        method
      })
      // Update the front-end to reflect the successful action
      dispatch(userFollowingChange(user.id, toggledFollowing))
      dispatch(userFollowingToast(user, toggledFollowing))
      // Tell the extension our friends have changed
      extension.clearUserStateCache()
      // Send tracking call
      await businessEvents.trackSegment(`${toggleAction}-person`, {
        'user_id': user.id
      }, {
        pageContext
      })
    } catch (err) {
      // Revert the front-end following state
      dispatch(userFollowingChange(user.id, user.isFollowing))
      // Inform the user that their action was unsuccessful
      dispatch(confirmModalActions.alert({
        heading: `Friend status could not be updated.`,
        subheading: `Something went wrong on our end. Please try again.`
      }))
      throw err
    }
  }
}

// Helpers

function navigateToUserHref(hrefFunc) {
  // Creates a navigation action dispatcher from a href helper function
  return function ({ event, pageContext, user }) {
    return function (dispatch) {
      dispatch(navigate({
        event,
        pageContext,
        href: hrefFunc(user)
      }))
    }
  }
}

function userProfileHref(user) {
  return `/${user.username}`
}

function userProfileCollectionsHref(user) {
  return `${userProfileHref(user)}/collections`
}

function userProfileCollectionsCreatedHref(user) {
  return `${userProfileCollectionsHref(user)}/created`
}

function userProfileCollectionsSubscribedHref(user) {
  return `${userProfileCollectionsHref(user)}/subscribed`
}

function userProfilePostsHref(user) {
  return `${userProfileHref(user)}/posts`
}

function userProfilePostModalHref(user, urlId) {
  return `${userProfileHref(user)}/posts?modal=1&url_id=${urlId}`
}

function userProfileFollowersHref(user) {
  return `${userProfileHref(user)}/followers`
}

function userProfileFollowingHref(user) {
  return `${userProfileHref(user)}/following`
}

function sharedHref() {
  return `/shared`
}

function userSharedHref(user) {
  return `/shared/${user.username}`
}

module.exports = {
  // Enums
  USER_FOLLOWING_CHANGE_PENDING,
  // Types
  USER_FOLLOWING_CHANGE,
  // Dispatchers
  showUserActionsModal,
  showUserShareModal,
  toggleFollowing,
  // Helpers
  sharedHref,
  userProfileHref,
  userProfileCollectionsHref,
  userProfileCollectionsCreatedHref,
  userProfileCollectionsSubscribedHref,
  userProfileFollowersHref,
  userProfileFollowingHref,
  userProfilePostsHref,
  userProfilePostModalHref,
  userSharedHref
}

// Define navigation dispatchers
Object.entries({
  navigateToShared: sharedHref,
  navigateToUserProfile: userProfileHref,
  navigateToUserProfileCollections: userProfileCollectionsHref,
  navigateToUserProfileCollectionsCreated: userProfileCollectionsCreatedHref,
  navigateToUserProfileCollectionsSubscribed: userProfileCollectionsSubscribedHref,
  navigateToUserProfileFollowers: userProfileFollowersHref,
  navigateToUserProfileFollowing: userProfileFollowingHref,
  navigateToUserProfilePosts: userProfilePostsHref,
  navigateToUserProfilePostModal: userProfilePostModalHref,
  navigateToUserShared: userSharedHref
}).forEach(([ key, value ]) => {
  module.exports[key] = navigateToUserHref(value)
})
